/**
 * Injection-prevention policy for untrusted field values.
 *
 * Form values are attacker-controlled more often than not: pasted text,
 * restored drafts (localStorage is writable by any script on the origin),
 * AI-generated payloads, API prefills. Modyra never renders values as HTML
 * (see SECURITY.md), but values still leak into places where invisible or
 * markup characters are dangerous: emails, PDF exports, logs, CSV files,
 * downstream systems that DO render HTML, and phishing-style UI spoofing
 * via bidi/zero-width characters.
 *
 * This module is the pure, zero-dependency heart of the feature: the form
 * engine wires it at the single write choke point (the field value signal)
 * so every entry path — user input, `patch`/`setValue`, draft restore,
 * array operations — is covered by construction.
 */
// Control chars except \t \n, DEL+C1, zero-width, line/paragraph separators,
// bidi overrides and isolates.
const MDY_TEXT_STRIP_RE = 
// eslint-disable-next-line no-control-regex -- matching control characters is the whole point of this regex
/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u200B-\u200D\u2028-\u202E\u2066-\u2069\uFEFF]/g;
const MDY_STRICT_EXTRA_RE = /[<>`]/g;
function isPlainObject(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
function stripProfileChars(text, profile) {
    let out = text.replace(MDY_TEXT_STRIP_RE, "");
    if (profile === "strict") {
        out = out.replace(MDY_STRICT_EXTRA_RE, "");
    }
    return out;
}
function truncateCodePoints(text, max) {
    // Code-point aware so a surrogate pair is never split.
    return Array.from(text).slice(0, max).join("");
}
/**
 * Applies `fn` to every string inside `value` (deep through plain objects
 * and arrays, cycle-safe). Reference-preserving: when nothing changes the
 * original reference is returned, so signal identity checks keep working.
 */
function mapStringsDeep(value, fn, seen) {
    if (typeof value === "string")
        return fn(value);
    if (Array.isArray(value)) {
        if (seen.has(value))
            return value;
        seen.add(value);
        let changed = false;
        const next = value.map(item => {
            const mapped = mapStringsDeep(item, fn, seen);
            if (!Object.is(mapped, item))
                changed = true;
            return mapped;
        });
        return changed ? next : value;
    }
    if (isPlainObject(value)) {
        if (seen.has(value))
            return value;
        seen.add(value);
        let changed = false;
        const next = {};
        for (const [key, item] of Object.entries(value)) {
            const mapped = mapStringsDeep(item, fn, seen);
            if (!Object.is(mapped, item))
                changed = true;
            next[key] = mapped;
        }
        return changed ? next : value;
    }
    return value;
}
/**
 * Applies a sanitizer and length cap to a field value — the pure unit the
 * engine calls on every write. Built-in profiles map over every string in
 * the value (deep); a custom sanitizer receives the whole value, then the
 * length cap is applied to its result.
 */
export function applyValueSecurity(value, options) {
    const { sanitizer, maxValueLength } = options;
    const actions = [];
    let out = value;
    if (typeof sanitizer === "function") {
        try {
            out = sanitizer(out);
            if (!Object.is(out, value)) {
                actions.push({
                    kind: "sanitized",
                    detail: "Custom sanitizer modified the value.",
                });
            }
        }
        catch (e) {
            // Custom sanitizer threw: report the error but keep the original value
            // so the form doesn't crash. The violation hook can route this to
            // telemetry/logging (see form-engine._report for the same pattern).
            actions.push({
                kind: "sanitizer-error",
                detail: `Custom sanitizer threw: ${e instanceof Error ? e.message : String(e)}`,
            });
            // out remains unchanged (value is preserved)
        }
    }
    else if (sanitizer !== "off") {
        const mapped = mapStringsDeep(out, text => stripProfileChars(text, sanitizer), new WeakSet());
        if (!Object.is(mapped, out)) {
            actions.push({
                kind: "sanitized",
                detail: `Profile "${sanitizer}" stripped invisible/markup characters.`,
            });
            out = mapped;
        }
    }
    if (maxValueLength !== undefined && maxValueLength >= 0) {
        const mapped = mapStringsDeep(out, (text) => text.length > maxValueLength
            ? truncateCodePoints(text, maxValueLength)
            : text, new WeakSet());
        if (!Object.is(mapped, out)) {
            actions.push({
                kind: "max-length",
                detail: `String truncated to ${maxValueLength} characters.`,
            });
            out = mapped;
        }
    }
    return { value: out, actions };
}
/**
 * Shape check for restored draft entries: the stored value must be
 * type-compatible with the field's declared initial value. `null` is
 * always accepted (Modyra's empty sentinel); a `null` initial accepts any
 * JSON-shaped value (the field's type is unconstrained). Anything
 * reachable only from live objects (File/Blob, functions, class
 * instances) can never legitimately come back from a JSON draft.
 */
export function draftShapeMatches(initial, value) {
    if (value === null)
        return true;
    if (initial === null || initial === undefined) {
        // Unknown type: accept JSON shapes only (no functions/symbols — though
        // JSON.parse cannot produce them, drafts may arrive from custom code).
        const t = typeof value;
        return (t === "string" || t === "number" || t === "boolean" ||
            Array.isArray(value) || isPlainObject(value));
    }
    if (Array.isArray(initial))
        return Array.isArray(value);
    if (isPlainObject(initial))
        return isPlainObject(value);
    const t = typeof initial;
    if (t === "string" || t === "number" || t === "boolean") {
        return typeof value === t;
    }
    // File/Blob/Date/class instances: a JSON draft can never restore these.
    return false;
}
